import {
  AnchoredPanelType,
  FieldWrapper,
  FormattedString,
  FormInputGroupLayout,
  FormValues,
  getValue,
  getValuesByFieldPrefix,
  InputFormGroup,
  InputFormSidePanel,
  InputFormSidePanelComponent,
  InputFormSidePanelProps,
  IpInput,
  RadioGroup,
  RadioGroupOption,
  SimpleBuilder,
  TextInput,
} from "o4a-react";
import React from "react";
import { mergeStyleSets } from "@fluentui/react";
import * as Messages from "../../codegen/Messages";
import { vnetTroubleshooting } from "../../constants/docConstants";
import { AzureVirtualNetworkSummary } from "../../gen/clients/mchub-azure-api-client";
import { FutureMcvcnExisting, FutureMcvcnNew, maxMcvcnCidrPrefix, minCidrPrefix } from "../../helpers/resourceHelper";
import { validateIpAddressWithinRanges } from "../../helpers/validationHelper";
import { Fields as OciCidrFields, OciCidrBuilder } from "../OciCidr/OciCidrBuilder";
import { VNETSelect } from "../VNETSelect/VNETSelect";

export const enum Fields {
  CidrBlocks = "CidrBlocks",
  FlowType = "FlowType",
  Name = "Name",
  NetworkVirtualAppliance = "NetworkVirtualAppliance",
  SelectedVnet = "SelectedVnet",
  OciSubnetOcid = "OciSubnetOcid",
  OciSubnetOcids = "OciSubnetOcids",
  OciVcnOcid = "OciVcnOcid",
  Vnet = "Vnet",
}

export const enum FlowType {
  CreateNew = "CreateNew",
  RegisterExisting = "RegisterExisting",
}

export interface McvcnCreateSidePanelProps extends
  Pick<InputFormSidePanelProps, "anchorId" | "onClose"> {
  onSubmit: (futureMcvcn: FutureMcvcnExisting | FutureMcvcnNew) => void;
  subscriptionId: string;
  location: string;
}

const classNames = mergeStyleSets({ subnets: { minWidth: "324px" } });

const minCidrBlocks = 1;
const maxCidrBlocks = 1; // due to issues, mcvcn only supports 1 cidr for now
const minSubnets = 1;
const maxSubnets = 2;

export const McvcnCreateSidePanel = ({
  anchorId,
  onClose,
  onSubmit,
  location,
  subscriptionId,
}: McvcnCreateSidePanelProps): JSX.Element => {
  const [formRef, setFormRef] = React.useState<InputFormSidePanelComponent>();
  const [selectedVnet, setSelectedVnet] = React.useState<AzureVirtualNetworkSummary | undefined>(undefined);
  const [flowType, setFlowType] = React.useState<FlowType>(FlowType.CreateNew);

  const flowTypeOptions: RadioGroupOption[] = [
    { id: FlowType.CreateNew, text: Messages.labels.createNewVcn() },
    { id: FlowType.RegisterExisting, text: Messages.labels.registerExistingOciVcn() },
  ];

  const onFlowTypeChanged = (value: string): void => setFlowType(value as FlowType);

  const onChangeVnet = (_: string, vnet: AzureVirtualNetworkSummary | undefined): void => {
    setSelectedVnet(vnet);
  };

  const internalOnSubmit = (formValues: FormValues): void => {
    // This is short circuited here because we now do not have the flowType as part of the form currently
    const flow = getValue(formValues, Fields.FlowType, InputFormGroup)?.id || flowType;
    const name = getValue(formValues, Fields.Name, InputFormGroup);
    const azureAttachedNetworkId = getValue(formValues, Fields.Vnet, InputFormGroup)?.[0].id;
    const customerNvaIpAddress = getValue(formValues, Fields.NetworkVirtualAppliance, InputFormGroup);

    if (flow === FlowType.CreateNew) {
      const simpleBuilderCidrs = getValue(formValues, Fields.CidrBlocks, InputFormGroup) as Record<string, string>[];
      const ociVcnCidrBlocks = simpleBuilderCidrs?.map(value => value[OciCidrFields.Address]).filter(value => !!value);

      onSubmit({
        name,
        azureAttachedNetworkId,
        ociVcnCidrBlocks,
        customerNvaIpAddress,
      } as FutureMcvcnNew);
    } else if (flow === FlowType.RegisterExisting) {
      const subnetOcids = getValue(formValues, Fields.OciSubnetOcids, InputFormGroup) as Record<string, string>[];
      const ociSubnetOcids = subnetOcids?.map(value => value[Fields.OciSubnetOcid]);

      const ociVcnOcid = getValue(formValues, Fields.OciVcnOcid, InputFormGroup);

      onSubmit({
        name,
        azureAttachedNetworkId,
        ociVcnOcid,
        ociSubnetOcids,
        customerNvaIpAddress,
      } as FutureMcvcnExisting);
    }

    onClose?.();
  };

  const requiredValidator = (simpleBuilderValues?: Record<string, string>[]): string[] | undefined => {
    const values = simpleBuilderValues
      ?.map(value => value[Fields.OciSubnetOcid])
      .filter(value => !!value);

    if (!values || values.length < minSubnets) {
      return [Messages.validation.required()];
    }

    return undefined;
  };

  const uniqueValidator = (ocid: string): string[] | undefined => {
    const simpleBuilderValues = getValuesByFieldPrefix(
      formRef?.getFormValues() ?? {},
      `${Fields.OciSubnetOcid}-`,
      InputFormGroup,
    );

    const values = Object.values(simpleBuilderValues);

    const index = values.indexOf(ocid);
    const nextIndex = values.indexOf(ocid, index + 1);

    if (nextIndex > index) {
      return [Messages.validation.unique()];
    }

    return undefined;
  };

  const nvaWithinVnetValidator = (value: string): string[] | undefined => {
    const errors: string[] = [];

    if (value) {
      // NVA IP address must be within the Azure Virtual Network address spaces
      errors.push(
        ...(validateIpAddressWithinRanges(
          value,
          selectedVnet?.addressSpaces || [],
        ) || []),
      );
    }

    return errors.length > 0 ? errors : undefined;
  };

  const formValues = formRef?.getFormValues() ?? {};

  const ociCidrs = getValue(formValues, Fields.CidrBlocks, InputFormGroup) as Record<string, string>[];
  const defaultOciCidrs = (ociCidrs?.filter(value => !!value[OciCidrFields.Address]) ?? [])
    .map(value => ({ [OciCidrFields.Address]: value[OciCidrFields.Address] }));

  const vcnOcid = getValue(formValues, Fields.OciVcnOcid, InputFormGroup);

  const subnetOcids = getValue(formValues, Fields.OciSubnetOcids, InputFormGroup) as Record<string, string>[];
  const defaultSubnetOcids = subnetOcids?.filter(value => !!value[Fields.OciSubnetOcid]) ?? [];

  return (
    <InputFormSidePanel
      componentRef={setFormRef}
      type={AnchoredPanelType.MEDIUM}
      layout={FormInputGroupLayout.WIDE}
      title={Messages.createNewPanels.mcvcn.title()}
      anchorId={anchorId}
      onSubmit={internalOnSubmit}
      onClose={onClose}
    >
      <TextInput
        required
        fieldName={Fields.Name}
        label={Messages.labels.name()}
      />
      <VNETSelect
        required
        fieldName={Fields.Vnet}
        label={Messages.labels.virtualNetwork()}
        tooltip={
          FormattedString(
            { inputText: Messages.hints.tooltipVNET(vnetTroubleshooting) },
          ) as unknown as string
        }
        subscriptionId={subscriptionId}
        location={location}
        onChange={onChangeVnet}
      />
      <IpInput
        disabled={!selectedVnet}
        validator={nvaWithinVnetValidator}
        fieldName={Fields.NetworkVirtualAppliance}
        label={Messages.labels.networkVirtualAppliance()}
        tooltip={Messages.hints.tooltipNetworkVirtualAppliance()}
        validation={{
          containsIpAddress: true,
          containsCidrBlock: false,
        }}
      />
      {false && (
        // TODO: Register new mcvcn not working on back end - hide this selection for now
        <FieldWrapper>
          <RadioGroup
            defaultValue={flowType}
            fieldName={Fields.FlowType}
            options={flowTypeOptions}
            onChange={onFlowTypeChanged}
            required
          />
        </FieldWrapper>
      )}
      {flowType === FlowType.CreateNew && (
        <OciCidrBuilder
          required
          disabled={!selectedVnet}
          defaultValue={defaultOciCidrs}
          label={Messages.labels.ociCidrBlocks()}
          fieldName={Fields.CidrBlocks}
          maxRows={maxCidrBlocks}
          minRows={minCidrBlocks}
          defaultNumberOfRows={minCidrBlocks}
          excludeCidrs={selectedVnet?.addressSpaces}
          groupName={InputFormGroup}
          minPrefix={minCidrPrefix}
          maxPrefix={maxMcvcnCidrPrefix}
        />
      )}
      {flowType === FlowType.RegisterExisting && (
        <>
          <TextInput
            defaultValue={vcnOcid}
            fieldName={Fields.OciVcnOcid}
            label={Messages.labels.vcnOcid()}
            required
          />
          <FieldWrapper
            label={Messages.labels.subnetOcids()}
            required
          >
            <SimpleBuilder
              defaultValue={defaultSubnetOcids}
              fieldName={Fields.OciSubnetOcids}
              maxRows={maxSubnets}
              minRows={minSubnets}
              defaultNumberOfRows={minSubnets}
              validator={requiredValidator}
            >
              <TextInput
                validator={uniqueValidator}
                className={classNames.subnets}
                fieldName={Fields.OciSubnetOcid}
                label={Messages.labels.ocid()}
              />
            </SimpleBuilder>
          </FieldWrapper>
        </>
      )}
    </InputFormSidePanel>
  );
};
